import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {Ess} from "../../../interfaces/ess/Ess";
import {EntityProperty} from "../../../interfaces/EntityProperty";

import ToastService from "../../../service/ToastService";
import apiV2 from "../../../service/api/ApiV2";
import {EntityTypeEnum} from "../../../enums/EntityTypeEnum";
import {getErrorMessage} from "../../../service/AuthenticatedApi";
import i18next from "i18next";

interface EssState {
  data: Ess[];
  loading: boolean;
  error: string | null;
}

const initialState: EssState = {
  data: [],
  loading: false,
  error: null,
};

export const updateEssProperty = createAsyncThunk<
  {essData: EntityProperty[]; essId: string},
  {essId: string; essData: EntityProperty[]},
  {rejectValue: string | null}
>("ess/updateEssProperty", async ({essId, essData}, {rejectWithValue}) => {
  try {
    await apiV2.updateEntityProperty(EntityTypeEnum.ESS, essId, essData);
    return {essData, essId};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.essPropertyError")));
  }
});

const getEssFromState = (state: EssState, essId: string): Ess => {
  const index = state.data.findIndex((x: Ess) => x.id === essId);
  return state.data[index];
};

const essSlice = createSlice({
  name: "Ess",
  initialState,
  reducers: {
    setEss: (state, action) => {
      state.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateEssProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEssProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateEssProperty.fulfilled, (state, action) => {
        state.loading = false;
        const {essData, essId} = action.payload;

        const ess = getEssFromState(state, essId);
        essData.forEach((property) => {
          (ess as any)[property.name] = property.value;
        });
        if (essData.length === 1) {
          ToastService.success(i18next.t("scada:plant.updates.essProperty", {count: 1, names: essData[0].name}));
        } else {
          ToastService.success(
            i18next.t("scada:plant.updates.essProperty", {
              count: essData.length,
              names: essData.map((p) => p.name).join(", "),
            }),
          );
        }
      });
  },
});

export const {setEss} = essSlice.actions;

export default essSlice.reducer;
